"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormDescription, FormMessage } from "@/components/ui/form";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options: Option[];
  placeholder?: string;
  description?: string;
  disabled?: boolean;
}

export function MultiSelectField<T extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholder,
  description,
  disabled = false,
}: MultiSelectFieldProps<T>) {
  const [open, setOpen] = React.useState(false);
  const { field } = useController({ name, control });

  // Ensure value is always an array of strings
  const value: string[] = Array.isArray(field.value) ? field.value.map(String) : [];

  const handleUnselect = (item: string) => {
    field.onChange(value.filter((i) => i !== item));
  };

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem className="flex flex-col">
          <FormLabel className="text-sm font-medium text-foreground">{label}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between hover:bg-muted/50 border-input bg-background h-auto min-h-10 py-2"
                disabled={disabled}
                style={{ borderColor: "hsl(240 20% 26%)" }}
              >
                <div className="flex flex-wrap gap-1 items-center">
                  {value.length > 0 ? (
                    value.map((item) => (
                      <Badge
                        variant="secondary"
                        key={item}
                        className="mr-1 mb-1 pr-1 bg-secondary/80 text-secondary-foreground border-none flex items-center gap-1 group"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnselect(item);
                        }}
                      >
                        {options.find((option) => option.value === item)?.label || item}
                        <X className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      {placeholder || `Select ${label}...`}
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0" align="start">
              <Command className="w-full">
                <CommandInput placeholder={`Search ${label}...`} className="h-9" />
                <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
                <CommandGroup>
                  <CommandList className="max-h-64">
                    {options.map((option) => {
                      const isSelected = value.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => {
                            const newValue = isSelected
                              ? value.filter((v) => v !== option.value)
                              : [...value, option.value];
                            field.onChange(newValue);
                          }}
                          className="cursor-pointer"
                        >
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary transition-colors",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible"
                            )}
                          >
                            <Check className={cn("h-4 w-4")} />
                          </div>
                          <span>{option.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
